import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { BUTTON_STATES } from 'components/shared/button';
import { URL_PARAMS, SUBSCRIBE_HOME_FORM_ID } from 'constants/forms';
import MainContext from 'context/main';
import useSegmentAnonId from 'hooks/use-segment-anon-id';
import translations from 'i18n';
import ErrorSvg from 'icons/error.inline.svg';
import LoadingSvg from 'icons/loading.inline.svg';
import SuccessSvg from 'icons/success.inline.svg';
import getParamsValue from 'utils/get-params-value';
import mutinyIdentify from 'utils/mutiny-identification';
import sendGravityFormData from 'utils/send-gravity-form-data';

const appearAndExitAnimationVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

const Form = ({ buttonText }) => {
  const {
    language: { locale },
  } = useContext(MainContext);

  const validationSchema = yup.object().shape({
    email: yup
      .string()
      .trim()
      .email(translations[locale].form.pleaseProvideValidEmail)
      .required(translations[locale].form.email + translations[locale].form.isRequiredField),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: yupResolver(validationSchema) });
  const [isClicked, setIsClicked] = useState(false);
  const [buttonState, setButtonState] = useState(BUTTON_STATES.DEFAULT);
  const { getSegmentAnonId, SegmentAnonIdInput } = useSegmentAnonId();

  const onSubmit = async (values) => {
    const { email } = values;
    const urlParams = getParamsValue(URL_PARAMS);
    const segmentAnonIdC = getSegmentAnonId();
    setButtonState(BUTTON_STATES.LOADING);

    mutinyIdentify({ email });

    try {
      await sendGravityFormData(SUBSCRIBE_HOME_FORM_ID, {
        input_1: email,
        input_2: urlParams.utm_source,
        input_3: urlParams.utm_medium,
        input_4: urlParams.utm_campaign,
        input_5: urlParams.utm_term,
        input_6: urlParams.utm_content,
        input_7: urlParams.gclid,
        input_8: segmentAnonIdC,
      });
      navigate(`https://app.revenuecat.com/signup?email=${email}`);
    } catch (error) {
      setButtonState(BUTTON_STATES.ERROR);
    }
  };

  const handleClick = () => {
    setIsClicked(true);
  };

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <form
      className={clsx(
        'relative flex w-full max-w-[440px] shrink-0 rounded-[100px] border pr-2 shadow-[0px_6px_16px_0px_rgba(61,75,143,0.15)] transition-colors duration-200 md:mx-auto md:mb-5 md:pr-0',
        isClicked ? 'border-secondary-2' : 'border-[#EAEDF6]',
        locale === 'ca' && '!max-w-full'
      )}
      onSubmit={handleSubmit(onSubmit)}
      onClick={handleClick}
      onFocus={handleClick}
      onBlur={() => setIsClicked(false)}
    >
      <input
        className="remove-autocomplete-styles py-4.5 h-16 w-full flex-grow border-none bg-transparent px-6 font-light text-new-gray-1 outline-none placeholder:bg-[linear-gradient(90deg,#EAC8F2_0%,#E6ADEC_32.41%,#D49EE8_71.97%,#CA97E6_100%)] placeholder:bg-clip-text placeholder:opacity-80 focus:outline-none focus:ring-0 xl:h-16 xl:py-3 lg:h-[60px] md:h-11 md:px-6"
        type="email"
        name="email"
        id="email"
        autoComplete="email"
        placeholder={translations[locale].form.yourEmailAddress}
        disabled={buttonState === BUTTON_STATES.LOADING || buttonState === BUTTON_STATES.SUCCESS}
        {...register('email')}
      />
      <SegmentAnonIdInput />
      <LazyMotion features={domAnimation}>
        <AnimatePresence>
          <m.button
            className={clsx(
              'hover:drop-shadow-button-hover flex h-12 shrink-0 items-center justify-center self-center whitespace-nowrap rounded-[40px] bg-secondary-2 font-sans2 text-base font-medium leading-none tracking-tight-2 text-white transition-[background-color,filter] hover:bg-blue-hover md:absolute md:-bottom-[53px] md:left-0 md:right-0 md:h-11 md:!w-full md:px-0',
              buttonState === BUTTON_STATES.LOADING || buttonState === BUTTON_STATES.SUCCESS
                ? 'w-12 px-0'
                : 'w-[155px] px-[50px] lg:w-[142px]',
              locale === 'ca' && '!w-[278px]'
            )}
            type="submit"
            initial="initial"
            animate="animate"
            exit="exit"
            variants={appearAndExitAnimationVariants}
            disabled={
              buttonState === BUTTON_STATES.LOADING || buttonState === BUTTON_STATES.SUCCESS
            }
          >
            {buttonState === BUTTON_STATES.DEFAULT && (
              <m.span
                className=""
                initial="initial"
                animate="animate"
                exit="exit"
                variants={appearAndExitAnimationVariants}
              >
                {buttonText}
              </m.span>
            )}
            {buttonState === BUTTON_STATES.LOADING && (
              <m.span
                initial="initial"
                animate="animate"
                exit="exit"
                variants={appearAndExitAnimationVariants}
              >
                <span className="sr-only">Loading</span>
                <LoadingSvg className="h-[34px] w-[34px] fill-white" aria-hidden />
              </m.span>
            )}
            {buttonState === BUTTON_STATES.SUCCESS && (
              <m.span
                initial="initial"
                animate="animate"
                exit="exit"
                variants={appearAndExitAnimationVariants}
              >
                <span className="sr-only">Success</span>
                <SuccessSvg className="h-5 w-7" aria-hidden />
              </m.span>
            )}
            {buttonState === BUTTON_STATES.ERROR && (
              <m.span
                initial="initial"
                animate="animate"
                exit="exit"
                variants={appearAndExitAnimationVariants}
              >
                <span className="sr-only">Error</span>
                <ErrorSvg className="h-12 w-12" aria-hidden />
              </m.span>
            )}
          </m.button>
          {errors.email && (
            <m.span
              className="absolute top-[calc(100%+12px)] w-full text-center text-sm leading-tight text-new-primary-1 md:bottom-[calc(100%+4px)] md:top-auto"
              initial="initial"
              animate="animate"
              exit="exit"
              key="error-msg"
              variants={appearAndExitAnimationVariants}
            >
              {errors.email?.message}
            </m.span>
          )}
        </AnimatePresence>
      </LazyMotion>
    </form>
  );
};

Form.propTypes = {
  buttonText: PropTypes.string.isRequired,
};

export default Form;
